import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { getMonthSummary, getCategoryBreakdown, getAnnualData } from '../services/analyticsService'
import type { Category, AnnualMonth, MonthSummary } from '../types'

export function useAnalytics(
  householdId: string | null,
  month: number,
  year: number,
) {
  const [summary, setSummary]       = useState<MonthSummary | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [annual, setAnnual]         = useState<AnnualMonth[]>([])
  const [loading, setLoading]       = useState(false)
  const [error, setError]           = useState<string | null>(null)

  const fetch = useCallback(() => {
    if (!householdId) return
    setLoading(true)
    setError(null)
    Promise.all([
      getMonthSummary(householdId, month, year),
      getCategoryBreakdown(householdId, month, year),
      getAnnualData(householdId, year),
    ])
      .then(([s, cats, ann]) => {
        setSummary(s)
        setCategories(cats)
        setAnnual(ann)
      })
      .catch(err => setError((err as Error).message))
      .finally(() => setLoading(false))
  }, [householdId, month, year])

  useEffect(() => {
    fetch()
    if (!householdId) return

    const channel = supabase
      .channel(`analytics-${householdId}-${year}-${month}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'transactions', filter: `household_id=eq.${householdId}` },
        () => fetch(),
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [fetch, householdId, year, month])

  return { summary, categories, annual, loading, error, refetch: fetch }
}
